import { useEffect } from 'react';
import { getAccessToken, usePrivy } from '@privy-io/react-auth';

import useUserStore from '../stores/user.store';
import useAvailabilityStore from '../stores/availability.store';
import { login, renewRefreshToken, updateUserAttribution, verifyAccessToken } from '../services/auth.service';
import { verifyRefCode } from '../services/user.service';
import { handleNotificationsLogout } from '../serviceWorkerRegistration';
import { getDeviceId, removeDeviceId } from '../utils';

const useUserData = () => {
  const { ready: privyReady, user: privyUser, logout: logoutPrivy } = usePrivy();
  const user = useUserStore((state) => state.user);
  const initialized = useUserStore((state) => state.initialized);
  const setInitialized = useUserStore((state) => state.setInitialized);
  const setUser = useUserStore((state) => state.setUser);
  const setOpenModal = useAvailabilityStore((state) => state.setOpenModal);
  const setMessage = useAvailabilityStore((state) => state.setMessage);

  useEffect(() => {
    if (user && !user.isGuest) {
      removeDeviceId();
    }
  }, [user]);

  const getMe = async () => {
    try {
      const fishAuth = localStorage.getItem('fishAuth');
      if (!fishAuth) throw new Error('no access token');

      try {
        const res = await verifyAccessToken(fishAuth);
        const { user } = res.data;
        if (privyUser) {
          if (user.userPrivyId !== privyUser?.id) throw new Error('user not match');
        }

        setUser(user);
      } catch (err) {
        if (err.code === 429) {
          setMessage(err.message);
          setOpenModal(true);
          return { success: false, error: err.message };
        }
        if (err.message === 'user not match') throw err;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('no refresh token');

        const res = await renewRefreshToken(refreshToken);
        const { userInfo, tokens } = res.data;
        if (privyUser) {
          if (userInfo.userPrivyId !== privyUser?.id) throw new Error('user not match');
        }

        localStorage.setItem('fishAuth', tokens.access.token);
        localStorage.setItem('refreshToken', tokens.refresh.token);
        localStorage.setItem('userId', userInfo.id);
        setUser(userInfo);
      }

      return { success: true };
    } catch (err) {
      console.error(`----- error getme, ${err.message} -----`);
      if (err.code === 429) {
        setMessage(err.message);
        setOpenModal(true);
      }
      return { success: false, error: err.message };
    }
  };

  const loginFromUserPrivy = async () => {
    const token = await getAccessToken();
    const deviceId = getDeviceId();
    const res = await login({ privyToken: token, deviceId });
    const { user, tokens } = res.data;

    const attribution = localStorage.getItem('ff_attribution');
    if (attribution) {
      await updateUserAttribution(user.id, JSON.parse(attribution));
    }

    localStorage.setItem('fishAuth', tokens.access.token);
    localStorage.setItem('refreshToken', tokens.refresh.token);
    localStorage.setItem('userId', user.id);
    setUser(user);
  };

  const logout = async () => {
    handleNotificationsLogout(localStorage.getItem('fishAuth'));
    localStorage.removeItem('fishAuth');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    setUser(null);
  };

  const applyRefCode = () => {
    const refCode = localStorage.getItem('ff-ref-code');
    if (refCode) {
      verifyRefCode(refCode)
        .then(() => console.log(`verify ref code ok, code ${refCode}`))
        .catch((err) => console.error(`----- err verifyRefCode, code ${refCode}, err ${err.message} -----`))
        .finally(() => localStorage.removeItem('ff-ref-code'));
    }
  };

  const getUserInfo = async () => {
    if (!privyReady) return;
    try {
      if (privyUser) {
        if (
          !!privyUser?.wallet &&
          privyUser?.linkedAccounts.some((acc) => acc.connectorType === 'embedded' && acc.walletClientType === 'privy')
        ) {
          console.log(`----- detect privy user and wallet`);
          const { success } = await getMe();
          if (!success) {
            await loginFromUserPrivy();
          }

          applyRefCode();
        }
      } else {
        console.log(`----- no detect privy user`);
        const { success } = await getMe();
        if (success) {
          applyRefCode();
        } else {
          logout();
        }
      }
    } catch (err) {
      console.error(err);
      if (err.code === 429 || err.code === 403) {
        setMessage(err.message);
        setOpenModal(true);
        await logoutPrivy();
        logout();
      }
    }

    setInitialized(true);
  };

  useEffect(() => {
    getUserInfo();
  }, [privyReady, privyUser]);

  return { appReady: initialized && privyReady, user };
};

export default useUserData;
